import TabButtons from '@/components/atoms/TabButtons';
import CustomTable from '@/components/molecules/CustomTable';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import useCreatorApplications from '@/hooks/creator-applications/useCreatorApplications';
import CreatorApplicationsFilters from '@/modules/creator-application/CreatorApplicationsFilters';
import {
  CreatorApplicationStatusBadge,
  formatCreatorApplicationDate,
  getCreatorApplicantTypeLabel,
} from '@/modules/creator-application/creatorApplicationDisplay';
import { TSortingDirection } from '@/types';
import {
  CREATOR_APPLICANT_TYPES,
  CREATOR_APPLICATION_PAGE_SIZE_OPTIONS,
  CREATOR_APPLICATION_STATUSES,
  TCreatorApplicantType,
  TCreatorApplicationListItem,
  TCreatorApplicationPageSize,
  TCreatorApplicationStatus,
} from '@/types/CreatorApplication.types';
import { getUserRolesDisplay } from '@/utils/stringUtils';
import { HStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import React, { useMemo } from 'react';
import { SUPER_ADMIN_PAGE_SIZE } from '@/constants/numberVars';

const COLUMNS = ['Applicant', 'Email', 'Type', 'Roles', 'Submitted', 'Status'];

// Maps the sortable column header to the backend sort field. Only `submittedAt`
// is exposed for now; the backend also allows createdAt / updatedAt / status.
const SORT_FIELD_BY_COLUMN: Record<string, string> = { Submitted: 'submittedAt' };
const SORT_COLUMN_BY_FIELD: Record<string, string> = { submittedAt: 'Submitted' };
const SORTABLE_COLUMNS = ['Submitted'] as const;
const DEFAULT_SORT_FIELD = 'submittedAt';
const DEFAULT_SORT_DIRECTION: TSortingDirection = 'desc';
const SORT_PARAM_REGEX = /^submittedAt:(asc|desc)$/;

// Both params are optional so other parts of the app can navigate to this route
// (sidebar link, "back" button, row clicks) without supplying search params.
type CreatorApplicationsSearch = {
  status?: TCreatorApplicationStatus;
  q?: string;
  applicantType?: TCreatorApplicantType;
  sort?: string;
  pageSize?: TCreatorApplicationPageSize;
};

const DEFAULT_STATUS: TCreatorApplicationStatus = 'pending';
// This list already lets an admin pick from CREATOR_APPLICATION_PAGE_SIZE_OPTIONS and
// remembers it in the URL, so this is the starting point, not an override — their choice
// still wins. It reads the shared constant rather than restating its value, so the list
// cannot drift away from every other admin table.
const DEFAULT_PAGE_SIZE: TCreatorApplicationPageSize = SUPER_ADMIN_PAGE_SIZE;
const PAGE_SIZE_OPTIONS = CREATOR_APPLICATION_PAGE_SIZE_OPTIONS.map(String);

const CreatorApplications = () => {
  const { status: statusParam, q, applicantType, sort, pageSize: pageSizeParam } = Route.useSearch();
  const navigate = Route.useNavigate();

  const status = statusParam ?? DEFAULT_STATUS;
  const filterText = q ?? '';
  const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
  const listSearch = { status, q, applicantType, sort, pageSize };

  // Sort lives in the URL as `<field>:<dir>`; default to submittedAt desc (matches backend).
  const [sortField, sortDirection] = (
    sort && SORT_PARAM_REGEX.test(sort) ? sort : `${DEFAULT_SORT_FIELD}:${DEFAULT_SORT_DIRECTION}`
  ).split(':') as [string, TSortingDirection];
  const sortingColumn = SORT_COLUMN_BY_FIELD[sortField];

  const setStatus = (next: TCreatorApplicationStatus) => navigate({ search: (prev) => ({ ...prev, status: next }) });

  const setSearchText = (value: string) => navigate({ search: (prev) => ({ ...prev, q: value ? value : undefined }) });

  const setApplicantType = (value?: TCreatorApplicantType) =>
    navigate({ search: (prev) => ({ ...prev, applicantType: value }) });

  const setPageSize = (value: string) =>
    navigate({ search: (prev) => ({ ...prev, pageSize: Number(value) as TCreatorApplicationPageSize }) });

  const updateSortingColumn = (column: string) => {
    const field = SORT_FIELD_BY_COLUMN[column];
    if (!field) return;
    const nextDirection: TSortingDirection = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    navigate({ search: (prev) => ({ ...prev, sort: `${field}:${nextDirection}` }) });
  };

  const {
    isLoading,
    isRefetching,
    pagination,
    data: applications,
  } = useCreatorApplications({
    filterText,
    status,
    applicantType,
    sortBy: sortField,
    sortDir: sortDirection,
    pageSize,
  });

  const isSearching = useMemo(
    () => filterText.length >= MIN_CHARS_TO_DO_SEARCH && isRefetching,
    [filterText, isRefetching],
  );

  const dataWithId = useMemo(
    () =>
      applications?.map((application) => ({
        ...application,
        id: application._id,
      })) ?? [],
    [applications],
  );

  const tableListItems = useMemo(
    () =>
      dataWithId.map((application) => ({
        id: application._id,
        cells: [
          `${application.applicantName?.firstName ?? ''} ${application.applicantName?.lastName ?? ''}`.trim() || '/',
          application.applicantEmail ?? '/',
          getCreatorApplicantTypeLabel(application.applicantType),
          application.existingRoles?.length ? getUserRolesDisplay(application.existingRoles) : '/',
          formatCreatorApplicationDate(application.submittedAt),
          <CreatorApplicationStatusBadge key={application._id} status={application.status} />,
        ],
      })),
    [dataWithId],
  );

  return (
    <SidebarItemViewTemplate title="Creator Applications">
      <HStack width={'100%'} marginTop={'20px'} justifyContent={'space-between'}>
        <TabButtons
          options={[
            { onClick: () => setStatus('pending'), labelValue: 'pending', labelDisplay: 'Pending' },
            { onClick: () => setStatus('approved'), labelValue: 'approved', labelDisplay: 'Approved' },
            { onClick: () => setStatus('rejected'), labelValue: 'rejected', labelDisplay: 'Rejected' },
          ]}
          selectedOptionValue={status}
        />
        <CreatorApplicationsFilters
          searchText={filterText}
          applicantType={applicantType}
          isSearching={isSearching}
          onSearchChange={setSearchText}
          onApplicantTypeChange={setApplicantType}
        />
      </HStack>
      <CustomTable<TCreatorApplicationListItem[], string>
        noItemsMessage="No creator applications found"
        columns={COLUMNS}
        isLoading={isLoading}
        isRefetching={isRefetching}
        isSearching={isSearching}
        tableListItems={tableListItems}
        pagination={pagination}
        dataWithId={dataWithId}
        indexToCopyToClipboard={1}
        customColumnGridPercentages={[22, 24, 14, 18, 12, 10]}
        columnsToSortBy={SORTABLE_COLUMNS}
        sortingColumn={sortingColumn}
        sortingDirection={sortDirection}
        updateSortingColumn={updateSortingColumn}
        pageSizeSelector={{
          selectedOption: String(pageSize),
          options: PAGE_SIZE_OPTIONS,
          onOptionClick: setPageSize,
        }}
        fullWidth
        hasRowSelect
        navigateOnRowSelect={false}
        onRowSelect={(applicationId) => {
          if (!applicationId) return;
          navigate({
            to: '/creator-applications/$applicationId',
            params: { applicationId },
            search: listSearch,
          });
        }}
      />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/creator-applications/')({
  component: CreatorApplications,
  validateSearch: (search: Record<string, unknown>): CreatorApplicationsSearch => {
    const result: CreatorApplicationsSearch = {};

    if (CREATOR_APPLICATION_STATUSES.includes(search.status as TCreatorApplicationStatus)) {
      result.status = search.status as TCreatorApplicationStatus;
    }

    if (CREATOR_APPLICANT_TYPES.includes(search.applicantType as TCreatorApplicantType)) {
      result.applicantType = search.applicantType as TCreatorApplicantType;
    }

    const rawQ = typeof search.q === 'string' ? search.q.trim() : '';
    if (rawQ) {
      result.q = rawQ;
    }

    if (typeof search.sort === 'string' && SORT_PARAM_REGEX.test(search.sort)) {
      result.sort = search.sort;
    }

    const parsedPageSize = Number(search.pageSize);
    if (CREATOR_APPLICATION_PAGE_SIZE_OPTIONS.includes(parsedPageSize as TCreatorApplicationPageSize)) {
      result.pageSize = parsedPageSize as TCreatorApplicationPageSize;
    }

    return result;
  },
});
